import { Fragment } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Box, ButtonBase, Icon, styled } from "@mui/material";
import useSettings from "app/hooks/useSettings";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { MatxLayoutSettings } from "../MatxLayout/settings";

// STYLED COMPONENTS
const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  display: mode === "compact" && "none",
  color: theme.palette.text.secondary,
}));

const ExtAndIntCommon = {
  display: "flex",
  overflow: "hidden",
  borderRadius: "4px",
  height: 44,
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 150ms ease-in",
  lineHeight: "27px",
  "&:hover": {
    background: MatxLayoutSettings.palette.lightGrey,
    color: MatxLayoutSettings.palette.active,
    "& .icon, & .sidenavHoverShow": {
      color: MatxLayoutSettings.palette.active,
    },
  },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important",
  },
  "& .icon": {
    fontWeight: 600,
    paddingLeft: "16px",
    paddingRight: "26px",
    verticalAlign: "middle",
  },
};

const ExternalLink = styled("a")(({ theme }) => ({
  ...ExtAndIntCommon,
  color: theme.palette.text.primary,
  fontSize: "16px",
  fontWeight: 600,
}));

const InternalLink = styled(Box)(({ theme }) => ({
  "& a": {
    ...ExtAndIntCommon,
    color: theme.palette.text.primary,
    fontSize: "16px",
    fontWeight: 600,
    "&.navItemActive": {
      backgroundColor: MatxLayoutSettings.palette.active,
      color: MatxLayoutSettings.palette.white,
      "&:hover": {
        backgroundColor: MatxLayoutSettings.palette.active,
        "& .icon, & .sidenavHoverShow": {
          color: MatxLayoutSettings.palette.white,
        },
      },
    },
    "&:not(.navItemActive):hover": {
      backgroundColor: MatxLayoutSettings.palette.lightGrey,
      color: MatxLayoutSettings.palette.active,
      "& .icon, & .sidenavHoverShow": {
        color: MatxLayoutSettings.palette.active,
      },
    },
  },
}));

const ActionButton = styled(ButtonBase)(({ theme }) => ({
  ...ExtAndIntCommon,
  width: '100%',
  textAlign: 'left',
  color: theme.palette.text.primary,
  fontSize: "16px",
  fontWeight: 600,
}));

const StyledText = styled(Span)(({ mode, customColor }) => ({
  fontSize: "16px",
  fontWeight: 600,
  paddingLeft: "0.8rem",
  display: mode === "compact" && "none",
  color: customColor || "inherit",
  opacity: 0.9,
  "&:hover": {
    color: "inherit",
  },
}));

const BulletIcon = styled("div")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  padding: "2px",
  marginLeft: "24px",
  marginRight: "8px",
  overflow: "hidden",
  borderRadius: "300px",
  background: theme.palette.text.primary,
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "300px",
}));

const BottomNavContainer = styled(Box)(() => ({
  flexGrow: 0,
  paddingBottom: "16px",
}));

const NavContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  flexGrow: 1,
  justifyContent: "space-between",
}));

const TopNavContainer = styled(Box)(() => ({
  flexGrow: 1,
  overflow: "auto",
}));

export default function MatxVerticalNav({ items }) {
  const { settings, updateSettings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const navigate = useNavigate();

  const updateSidebarMode = (sidebarSettings) => {
    let activeLayoutSettingsName = settings.activeLayout + "Settings";
    let activeLayoutSettings = settings[activeLayoutSettingsName];
    updateSettings({
      ...settings,
      [activeLayoutSettingsName]: {
        ...activeLayoutSettings,
        leftSidebar: {
          ...activeLayoutSettings.leftSidebar,
          ...sidebarSettings,
        },
      },
    });
  };

  const handleItemClick = (item) => {
    if (item && item.onClick) {
      item.onClick();

      if (item.name === "Log Out" && item.path) {
        navigate(item.path);
      }
    }

    if (window.innerWidth < 1200) {
      updateSidebarMode({ mode: "close" });
    }
  };

  const renderLevels = (data) => {
    return data.map((item, index) =>
      item.type === "label" ? (
        <ListLabel key={index} mode={mode} className="sidenavHoverShow">
          {item.label}
        </ListLabel>
      ) : item.children ? (
        <MatxVerticalNavExpansionPanel
          mode={mode}
          item={item}
          key={index}
          onClick={() => handleItemClick(item)}
        >
          {renderLevels(item.children)}
        </MatxVerticalNavExpansionPanel>
      ) : item.type === "extLink" ? (
        <Box key={index} mb={2}>
          <ExternalLink
            onClick={() => handleItemClick(item)}
            href={item.path}
            className={`${mode === "compact" && "compactNavItem"}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase key={item.name} name="child">
              {item.icon ? (
                <Icon
                  className="icon"
                  sx={{
                    width: 36,
                    opacity: 0.9,
                    color: item.style?.color || "inherit",
                    "&:hover": {
                      color: "inherit",
                    },
                  }}
                >
                  {item.icon}
                </Icon>
              ) : (
                <span
                  className="item-icon icon-text"
                  style={{ color: item.style?.color || "inherit" }}
                >
                  {item.iconText}
                </span>
              )}
              <StyledText
                mode={mode}
                className="sidenavHoverShow"
                customColor={item.style?.color || "inherit"}
              >
                {item.name}
              </StyledText>
              <Box mx="auto" />
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        </Box>
      ) : item.onClick ? (
        <Box key={index} mb={4}>
          <ActionButton
            onClick={() => handleItemClick(item)}
            className={`${mode === "compact" && "compactNavItem"}`}
          >
            {item.icon ? (
              <Icon
                className="icon"
                sx={{
                  width: 36,
                  opacity: 0.9,
                  color: item.style?.color || MatxLayoutSettings.palette.mediumBlack,
                  "&:hover": {
                    color: "inherit",
                  },
                }}
              >
                {item.icon}
              </Icon>
            ) : (
              <Fragment>
                <BulletIcon
                  className="nav-bullet"
                  sx={{ display: mode === "compact" && "none" }}
                />
                <Box
                  className="nav-bullet-text"
                  sx={{
                    ml: "20px",
                    fontSize: "11px",
                    display: mode !== "compact" && "none",
                  }}
                >
                  {item.iconText}
                </Box>
              </Fragment>
            )}
            <StyledText
              mode={mode}
              className="sidenavHoverShow"
              customColor={item.style?.color || MatxLayoutSettings.palette.mediumBlack}
            >
              {item.name}
            </StyledText>
            <Box mx="auto" />
            {item.badge && (
              <BadgeValue className="sidenavHoverShow">
                {item.badge.value}
              </BadgeValue>
            )}
          </ActionButton>
        </Box>
      ) : (
        <Box key={index} mb={4}>
          <InternalLink>
            <NavLink
              onClick={() => handleItemClick(item)}
              to={item.path}
              end
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === "compact" && "compactNavItem"}`
                  : `${mode === "compact" && "compactNavItem"}`
              }
            >

              {({ isActive }) => {
                const customColor = isActive
                  ? MatxLayoutSettings.palette.white
                  : item.style?.color || MatxLayoutSettings.palette.mediumBlack;
                const selectedBgColor = isActive
                  ? MatxLayoutSettings.palette.active
                  : "";
                return (
                  <ButtonBase
                    key={item.name}
                    name="child"
                    sx={{ width: "100%" }}
                    style={{
                      color: customColor,
                      backgroundColor: selectedBgColor,
                    }}
                  >
                    {item.icon ? (
                      <Icon
                        className="icon"
                        sx={{
                          width: 36,
                          opacity: 0.9,
                          color: customColor,
                        }}
                      >
                        {item.icon}
                      </Icon>
                    ) : (
                      <Fragment>
                        <BulletIcon
                          className="nav-bullet"
                          sx={{ display: mode === "compact" && "none" }}
                        />
                        <Box
                          className="nav-bullet-text"
                          sx={{
                            ml: "20px",
                            fontSize: "11px",
                            display: mode !== "compact" && "none",
                          }}
                        >
                          {item.iconText}
                        </Box>
                      </Fragment>
                    )}
                    <StyledText
                      mode={mode}
                      className="sidenavHoverShow"
                      customColor={customColor}
                    >
                      {item.name}
                    </StyledText>
                    <Box mx="auto" />
                    {item.badge && (
                      <BadgeValue className="sidenavHoverShow">
                        {item.badge.value}
                      </BadgeValue>
                    )}
                  </ButtonBase>
                );
              }}
            </NavLink>
          </InternalLink>
        </Box>
      )
    );
  };

  const hasBottomItems = items && items.main && items.bottom;

  if (hasBottomItems) {
    return (
      <NavContainer className="navigation">
        <TopNavContainer>
          {renderLevels(items.main)}
        </TopNavContainer>
        <BottomNavContainer>
          {renderLevels(items.bottom)}
        </BottomNavContainer>
      </NavContainer>
    );
  } else {
    return <div className="navigation">{renderLevels(items)}</div>;
  }
}